import isEqual from 'lodash.isequal';
import { useContext, useEffect, useMemo, useState } from 'react';
import TagId from '~/constants/common/TagId';
import { COOKIES } from '~/constants/srp';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useSavedSearches } from '~/context/common/SavedSearchesContext';
import StaticContext from '~/context/srp/StaticContext';
import useHasPriceDropFacetCount from '~/hooks/srp/selectors/useHasPriceDropFacetCount';
import decodeCvnaId from '~/utilities/cvnaId';
import { Dismissed, NewPriceFilter, PromoBanners } from '../constants';
import { setBannerDismissedCookie } from '../utils';

export default function useHandleShowLogic() {
  const { dismissedNewPrice } = useContext(StaticContext);
  const savedSearches = useSavedSearches();
  const {
    appliedFilters: { tagIds }
  } = useContext(AppliedFiltersContext);
  const [isPriceDropDismissed, setIsPriceDropDismissed] =
    useState(dismissedNewPrice);
  const hasPriceDropCount = useHasPriceDropFacetCount();

  const hasNewPriceFilter = useMemo(
    () => tagIds?.includes(TagId.PriceDrop.toString()) ?? false,
    [tagIds]
  );

  const hasNewPriceSavedSearch = useMemo(
    () =>
      savedSearches.some(savedSearch =>
        isEqual(decodeCvnaId(savedSearch.cvnaId).filters, NewPriceFilter)
      ),
    [savedSearches]
  );

  const shouldDismissPriceDrop = hasNewPriceFilter || hasNewPriceSavedSearch;

  useEffect(() => {
    if (shouldDismissPriceDrop) {
      if (hasNewPriceSavedSearch) {
        // Hide banner throughout the session if user has Price Drop saved search
        setBannerDismissedCookie(
          PromoBanners.PriceDrop,
          COOKIES.dismissedNewPriceBanner
        );
      }

      setIsPriceDropDismissed(Dismissed.Banner);
    }
  }, [hasNewPriceFilter, hasNewPriceSavedSearch, shouldDismissPriceDrop]);

  const shouldShow = useMemo(
    () =>
      isPriceDropDismissed === Dismissed.None &&
      !shouldDismissPriceDrop &&
      hasPriceDropCount,
    [hasPriceDropCount, isPriceDropDismissed, shouldDismissPriceDrop]
  );

  return {
    shouldShow,
    setIsPriceDropDismissed
  };
}
